import { inject } from '@angular/core';

import {
  type CanvasElementFrom,
  type IoConfig,
  type PropsOf,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import { AuthService } from '@blaze-shared/auth-data-access';
import {
  type BorderRadiusValue,
  type ContainerActionConfig,
  type ElementHorizontalAlignment,
  type ElementSizingMode,
  type FontOptions,
  type LabelIconAlignment,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
} from '@blaze-shared/elements-domain';
import { isColumnId } from '@blaze-shared/schema-domain';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';
import { ButtonValidationHandler } from '../button/button-validation-handler.service';

import { paymentV2Migration } from './migrations/002-payment.migration';
import { PaymentButtonComponent } from './payment-button.component';

export type PaymentButtonDef = WithProps<PaymentButtonPropsModel> & WithIoConfig;
export type PaymentButtonElement = CanvasElementFrom<'paymentButton'>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    paymentButton: PaymentButtonDef;
  }
}

const DEFAULT_WIDTH = 160;
const MIN_SIZE = 48;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed', 'auto'];

registerElementDefinition({
  componentType: PaymentButtonComponent,
  validationHandler: ButtonValidationHandler,
  name: 'Payment Button',
  type: 'paymentButton',
  defaults: getDefaults(),
  height: { mode: 'value', value: MIN_SIZE, fixed: true },
  ioConfig: getIoConfig(),
  isCustomerPortalOnly: true,
  isListed: true,
  propsConfig: getPropsConfig(),
  tags: ['checkout', 'foxy', 'payment', 'square', 'stripe', 'nmi'],
  toolbar: ['triggerClick'],
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  groups: ['advanced'],
  features: {
    loadingIndication: 'fadeWithoutIndicator',
  },
  version: 2,
  migrations: {
    2: paymentV2Migration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [{ eventType: 'click', schema: null }],
  };
}

type PropsModel = VisibilityElementProps;

export type PaymentButtonPropsModel = PropsModel & {
  content: {
    button: {
      label: string;
      icon?: string;
      iconPosition?: LabelIconAlignment;
    };
    action?: ContainerActionConfig;
    successAction?: ContainerActionConfig;
    payment?: {
      provider: string;
      catalogColumn: string;
      quantityColumn: string;
      referenceColumn: string;
    };
  };
  style: {
    label: {
      color: PropertiesConfigInputColorModel;
      align: ElementHorizontalAlignment;
    };
    font: FontOptions;
    button: {
      fill: PropertiesConfigInputColorModel;
      outline: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
    };
  };
};

function getDefaults(): PropsOf<'paymentButton'> {
  return {
    content: {
      button: {
        label: 'Payment',
      },
    },
    style: {
      label: {
        color: { color1: '$t.complementary', selectorType: 'solid' },
        align: 'center',
      },
      font: {
        style: [],
        fontFamily: '$t.fontFamily',
        lineHeight: '$t.lineHeight',
        fontSize: '$t.fontSize',
        fontWeight: '$t.labelFontWeight' as FontWeightOption,
      },
      button: {
        fill: { color1: '$t.accent', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'none' },
        radius: '$t.radius',
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('paymentButton', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'content.payment',
        'Payment Processor',
        [
          epc.fields.selectFieldFromApi(
            'content.payment.provider',
            'Provider',
            'LIST_PAYMENT_CONNECTORS',
            {
              showSelection: true,
              allowNone: false,
              hideLabel: true,
              placeholder: 'Select provider',
            },
          ),
        ],
        { hideLabel: false },
      ),

      epc.subsections.subsection(
        'content.payment',
        'Order Items',
        [
          epc.fields.selectDataSourceField(
            {
              key: 'value',
              label: 'Order Items',
              sources: 'list',
              bindings: [],
            },
            'payment',
            'Order Items',
            { hideLabel: true },
          ),
          ...epc.utils.disabledFields(
            [
              epc.fields.selectFieldFromDataBinding(
                `content.payment.catalogColumn`,
                'Item ID',
                'value',
                {
                  dataBindingsMode: 'first-dataset',
                  tooltip: 'ID of the item in the payment processing platform',
                  optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                `content.payment.quantityColumn`,
                'Quantity',
                'value',
                {
                  dataBindingsMode: 'first-dataset',
                  optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                },
              ),
              epc.fields.selectFieldFromDataBinding(
                `content.payment.referenceColumn`,
                'Reference',
                'value',
                {
                  dataBindingsMode: 'first-dataset',
                  tooltip: `The Reference number will be passed as an input to the 'When Paid' action.`,
                  optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                },
              ),
            ],
            (model) =>
              !getValueByPath(model, 'data.?key:value.dataSource')
                ? 'Order Items table is not selected'
                : undefined,
          ),
        ],
        {
          tooltip: 'Datasource containing payment provider item ids and quantities for each item.',
        },
      ),
      epc.subsections.subsection('content.button', 'Button', [
        epc.fields.textInput('content.button.label', 'Label'),
        epc.fields.iconField('content.button.icon'),
        epc.fields.iconPositionField('content.button.iconPosition', 'content.button.icon'),
      ]),

      epc.subsections.subsection(
        'content',
        'Action',
        [
          epc.utils.conditionalField(
            epc.utils.disabledField(
              epc.fields.buttonActionField('content.action', undefined, {
                tooltip:
                  'This automatically generated action is visible only to the root users and in most cases should not be changed.',
              }),
              (model) => {
                return !getValueByPath(model, 'content.payment.provider') ||
                  !getValueByPath(model, 'content.payment.catalogColumn') ||
                  !getValueByPath(model, 'content.payment.quantityColumn') ||
                  !getValueByPath(model, 'content.payment.referenceColumn')
                  ? 'Payment provider or order items are not configured'
                  : undefined;
              },
            ),
            'content.action',
            // Hide if not root user - payment action field available should be available for root users only
            () => !inject(AuthService).isCurrentUserAccountRootOrDev(),
          ),

          ...epc.utils.disabledFields(
            [
              epc.fields.buttonActionField('content.successAction', 'When Paid', {
                isServerWf: true,
                tooltip:
                  'Action that will be triggered after a sucessful payment. Use it to update the status of the order.',
                inputSchemas: (model) => {
                  const columnIdOrName = getValueByPath(
                    model,
                    'content.payment.referenceColumn',
                  ) as string;
                  const columnId = isColumnId(columnIdOrName) ? columnIdOrName : undefined;
                  return [
                    {
                      id: crypto.randomUUID(),
                      inputName: 'paymentData',
                      name: 'paymentData',
                      columns: [
                        {
                          columnType: 'String',
                          dataType: 'String',
                          id: columnId,
                          isProfilePicture: false,
                          name: columnIdOrName,
                        },
                      ],
                    },
                  ];
                },
              }),
            ],
            (model) => {
              return !getValueByPath(model, 'content.payment.provider') ||
                !getValueByPath(model, 'content.payment.catalogColumn') ||
                !getValueByPath(model, 'content.payment.quantityColumn') ||
                !getValueByPath(model, 'content.payment.referenceColumn') ||
                !getValueByPath(model, 'content.action')
                ? 'Payment provider or order items are not configured'
                : undefined;
            },
          ),
        ],
        { hideLabel: true },
      ),

      epc.subsections.visibilitySubsection(),

      epc.subsections.subsection('content.button', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
        epc.root.elementHeightField(
          { min: MIN_SIZE, max: MAX_HEIGHT, unit: 'px', step: 1 },
          HEIGHT_OPTIONS,
        ),
      ]),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.subsection('style.label', 'Label', [
          epc.fields.colorField('style.label.color', 'Label', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.horizontalAlignField('style.label.align'),
        ]),

        epc.subsections.elementFontSubsection('style.font'),

        epc.subsections.subsection('style.button', 'Button', [
          epc.fields.colorField('style.button.fill', 'Fill', { allowNone: false }),
          epc.fields.colorField('style.button.outline', 'Outline', { allowGradient: false }),
          epc.fields.radiusField('style.button.radius'),
        ]),
      ],
      true,
    ),
  ]);
}
